import type { SpriteRef } from '../../../data/gameData.ts';
import { GameBehaviour } from '../game.ts';
import type { SimGo, SpriteState } from '../world.ts';
import { num } from '../props.ts';
import { FrameCounter, clamp01, vlen, vnorm, type RGBA, type V2 } from '../unity.ts';

/**
 * DeadQuarterExploder: the dead player splits into four physical quarters that scatter, rest for
 * 1.8 s and fade out over 0.5 s. Only "ICheckpointable" so a checkpoint restore removes them.
 */
export class DeadQuarterExploder extends GameBehaviour {
  sourceSprite: SpriteState | null = null;
  minForce = 1;
  maxForce = 2;
  minTorque = -30;
  maxTorque = 30;
  despawnCounter = FrameCounter.seconds(1.8);
  fadeOutCounter = FrameCounter.seconds(0.5);

  override awake() {
    const d = this.data;
    this.minForce = num(d.minForce, 1);
    this.maxForce = num(d.maxForce, 2);
    this.minTorque = num(d.minTorque, -30);
    this.maxTorque = num(d.maxTorque, 30);
  }

  override start() {
    this.updateSpriteQuarters();
    const center = this.go.position;
    const rand = (a: number, b: number) => a + (b - a) * this.game.random();
    for (const g of this.go.descendants()) {
      const body = g.body;
      if (!body) continue;
      const p = g.position;
      const d = { x: p.x - center.x, y: p.y - center.y };
      const dir = vlen(d) > 1e-5 ? vnorm(d) : { x: 0, y: 0 };
      const force = rand(this.minForce, this.maxForce);
      body.addImpulse({ x: dir.x * force, y: dir.y * force });
      body.addTorqueImpulse(rand(this.minTorque, this.maxTorque));
    }
    this.despawnCounter.start();
  }

  override fixedUpdate() {
    if (this.despawnCounter.countCheckFinishReset()) {
      this.fadeOutCounter.start();
      for (const c of this.go.collidersInChildren()) c.enabled = false;
    }
    if (this.fadeOutCounter.isRunningNotFinished()) {
      const a = 1 - clamp01(this.fadeOutCounter.percentage);
      for (const g of this.go.descendants()) if (g.sprite) g.sprite.color = [g.sprite.color[0], g.sprite.color[1], g.sprite.color[2], a];
      if (this.fadeOutCounter.countCheckFinishReset()) this.go.destroy();
    }
  }

  private updateSpriteQuarters() {
    const src = this.sourceSprite?.sprite;
    if (!src) return;
    splitIntoQuarters(this.go, src, this.sourceSprite!.color);
  }
}

function splitIntoQuarters(go: SimGo, src: SpriteRef, color: RGBA) {
  const [rx, ry, rw, rh] = src.def.r;
  const halfW = rw / 2, halfH = rh / 2;
  for (let i = 0; i < 4; i++) {
    const child = go.findChild(`q${i + 1}`);
    if (!child?.sprite) continue;
    const offX = i % 2 === 0 ? 0 : halfW;
    const offY = i < 2 ? halfH : 0;
    const quarter: SpriteRef = {
      tex: src.tex,
      texDef: src.texDef,
      def: {
        ...src.def,
        r: [rx + offX, ry + offY, halfW, halfH],
        p: [clamp01((src.def.p[0] * rw - offX) / halfW), clamp01((src.def.p[1] * rh - offY) / halfH)],
        b: undefined,
      },
    };
    child.sprite.sprite = quarter;
    child.sprite.color = [...color] as RGBA;
  }
}

/** SpriteExploder: a broken tile splits into four quarters pushed away from the breaker, fading after 0.2 s. */
export class SpriteExploder extends GameBehaviour {
  sourceSprite: SpriteRef | null = null;
  sourceColor: RGBA = [1, 1, 1, 1];
  breakPos: V2 = { x: 0, y: 0 };
  minForce = 1;
  maxForce = 2;
  minTorque = -30;
  maxTorque = 30;
  despawnCounter = FrameCounter.seconds(0.2);
  fadeOutCounter = FrameCounter.seconds(0.5);

  override awake() {
    const d = this.data;
    this.minForce = num(d.minForce, 1);
    this.maxForce = num(d.maxForce, 2);
    this.minTorque = num(d.minTorque, -30);
    this.maxTorque = num(d.maxTorque, 30);
  }

  override start() {
    if (this.sourceSprite) splitIntoQuarters(this.go, this.sourceSprite, this.sourceColor);
    const rand = (a: number, b: number) => a + (b - a) * this.game.random();
    for (const g of this.go.descendants()) {
      const body = g.body;
      if (!body) continue;
      const p = body.position;
      const d = { x: p.x - this.breakPos.x, y: p.y - this.breakPos.y };
      const dir = vlen(d) > 1e-5 ? vnorm(d) : { x: 0, y: 0 };
      const force = rand(this.minForce, this.maxForce);
      body.addImpulse({ x: dir.x * force, y: dir.y * force });
      body.addTorqueImpulse(rand(this.minTorque, this.maxTorque));
    }
    this.despawnCounter.start();
  }

  override fixedUpdate() {
    if (this.despawnCounter.countCheckFinishReset()) this.fadeOutCounter.start();
    if (this.fadeOutCounter.isRunningNotFinished()) {
      const a = 1 - this.fadeOutCounter.percentage;
      for (const g of this.go.descendants()) if (g.sprite) g.sprite.color = [g.sprite.color[0], g.sprite.color[1], g.sprite.color[2], a];
      if (this.fadeOutCounter.countCheckFinishReset()) this.go.destroy();
    }
  }
}
